// Single source of truth for dashboard navigation (9-22 mockups).
//
// The sidebar renders from this. Adding or retiring a page here is the only
// change needed to offer or withdraw it in the UI.
//
// Shape: three top-level groups; "Detection +" (the Prevention Resources
// page, renamed 10-28; its route keeps the old name) nests four sub-groups.
// A group with its own `path` also owns a page - clicking its label navigates
// there as well as expanding it (Detection +).
// Groups without a path only expand / collapse.
//
// Pages absent from this list are intentionally not offered anywhere in the
// UI. Some remain routable by direct URL (the legacy Learn More pages, Home,
// Trust Center) - their content now lives in the Fraud Alerts hub.
export const DASHBOARD_NAV = [
  {
    type: 'group',
    id: 'red-flag-alerts',
    label: 'Red Flag Alerts',
    items: [
      // Hot Scam detail pages hang off the Fraud Alerts hub's Hot Scams tab
      // but live under their own path; `aliases` keeps this entry (and the
      // group) highlighted while reading one.
      // "CyberFraud Alert" (10-28): the hub of agency alerts (FBI / IC3, SSA,
      // IRS, FTC) plus ScamWire, the open-source scam reporting that used to be
      // called Hot Scams. "Explained" is its reading companion.
      { label: 'CyberFraud Alert', path: '/dashboard/fraud-alerts', aliases: ['/dashboard/hot-scams'] },
      { label: 'Explained', path: '/dashboard/cyberfraud-alert-explained' },
      { label: 'Social Media IQ', path: '/dashboard/social-media-iq' },
      { label: 'Abuse Playbook', path: '/dashboard/abuse-playbook' },
      { label: 'Threat Battlespace', path: '/dashboard/threat-battlespace' },
      { label: 'File a Complaint', path: '/dashboard/file-a-complaint' },
    ],
  },
  {
    type: 'group',
    id: 'prevention',
    label: 'Detection +',
    path: '/dashboard/prevention-resources',
    items: [
      {
        type: 'group',
        id: 'mosaic',
        label: 'MoSAIC PSYOPS',
        items: [
          { label: 'Explained', path: '/dashboard/mosaic-psyops-explained' },
          { label: 'Catfishing / Romance', path: '/dashboard/catfishing-romance' },
          { label: 'Pig Butchering / Crypto', path: '/dashboard/pig-butchering-crypto' },
          { label: 'The Day After', path: '/dashboard/the-day-after' },
        ],
      },
      {
        type: 'group',
        id: 'detection',
        label: 'Detection Tools',
        items: [
          { label: 'Deepfake', path: '/dashboard/deepfake' },
          { label: 'Image Search', path: '/dashboard/image-check' },
        ],
      },
      {
        type: 'group',
        id: 'av-library',
        label: 'Av Library',
        items: [
          { label: 'FBI / IC3', path: '/dashboard/av-library/fbi-ic3' },
          { label: 'SSA', path: '/dashboard/av-library/ssa' },
          { label: 'IRS', path: '/dashboard/av-library/irs' },
          { label: 'FTC', path: '/dashboard/av-library/ftc' },
          { label: 'Podcast', path: '/dashboard/av-library/podcast' },
        ],
      },
      {
        type: 'group',
        id: 'pulse',
        label: 'Pulse',
        items: [{ label: 'Sci Report', path: '/dashboard/sci-report' }],
      },
    ],
  },
  {
    type: 'group',
    id: 'my-noscam',
    label: 'My No / SCAM',
    items: [
      { label: 'Account Management', path: '/dashboard/account-management' },
      { label: 'Feedback', path: '/dashboard/feedback' },
      { label: 'eDiscovery', path: '/dashboard/ediscovery' },
      { label: 'Support', path: '/dashboard/support' },
    ],
  },
];

// Admin-only entry, pinned to the bottom of the sidebar for admins.
export const ADMIN_NAV_ITEM = { label: 'Admin', path: '/dashboard/admin' };

// A page path "belongs" to a nav path when it is that path or a child of it
// (/dashboard/fraud-alerts/123 belongs to /dashboard/fraud-alerts). Plain
// startsWith would also make /dashboard/mosaic-psyops claim
// /dashboard/mosaic-psyops-explained, which is a different page.
export function pathBelongsTo(pathname, navPath) {
  return pathname === navPath || pathname.startsWith(`${navPath}/`);
}

// True when `pathname` is this leaf's page (or one of its aliases).
export function itemMatchesPath(item, pathname) {
  return [item.path, ...(item.aliases || [])].some((p) => pathBelongsTo(pathname, p));
}

// True when `pathname` is this group's own page or any descendant page.
export function groupContainsPath(group, pathname) {
  if (group.path && pathBelongsTo(pathname, group.path)) return true;
  return group.items.some((item) =>
    item.type === 'group' ? groupContainsPath(item, pathname) : itemMatchesPath(item, pathname)
  );
}

// Ids of the groups to expand so that `pathname` is visible, outermost first.
export function openChainFor(pathname, groups = DASHBOARD_NAV) {
  for (const entry of groups) {
    if (entry.type !== 'group' || !groupContainsPath(entry, pathname)) continue;
    return [entry.id, ...openChainFor(pathname, entry.items)];
  }
  return [];
}

// Ids of every group nested inside `group` (not including itself).
export function descendantGroupIds(group) {
  return group.items.flatMap((item) =>
    item.type === 'group' ? [item.id, ...descendantGroupIds(item)] : []
  );
}
